import { useRef, useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, ChevronLeft } from 'lucide-react';
import type { LearningPath, NodeContent } from '@shared/types/learning-path';
import { trackEvent, getBaseProperties } from '@shared/services/analytics';
import { ANALYTICS_EVENTS } from '@shared/constants';
import { NodeContentRenderer } from './NodeContentRenderer';
import { ProgressTracker } from './ProgressTracker';
import { Button } from '../ui/Button';

interface LinearLayoutProps {
  path: LearningPath;
  nodeContents: Map<string, NodeContent>;
  currentNodeId: string | null;
  onNavigate: (nodeId: string) => void;
  onComplete: (nodeId: string) => void;
}

const SIDEBAR_COLLAPSED_KEY = 'cogniprint:learning-sidebar-collapsed';

function loadCollapsedPref(): boolean {
  if (typeof localStorage === 'undefined') return false;
  try { return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1'; } catch { return false; }
}

export function LinearLayout({ path, nodeContents, currentNodeId, onNavigate, onComplete }: LinearLayoutProps) {
  const sectionRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(loadCollapsedPref);

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  }, []);

  const currentIndex = path.nodes.findIndex((n) => n.nodeId === currentNodeId);
  const prevNode = currentIndex > 0 ? path.nodes[currentIndex - 1] : null;
  const nextNode = currentIndex < path.nodes.length - 1 ? path.nodes[currentIndex + 1] : null;

  useEffect(() => {
    if (currentNodeId) {
      const el = sectionRefs.current.get(currentNodeId);
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentNodeId]);

  return (
    <div className="flex flex-col lg:flex-row min-h-screen bg-dark-bg">
      {/* Sidebar (desktop) */}
      <div className="hidden lg:block relative shrink-0">
        <ProgressTracker
          path={path}
          currentNodeId={currentNodeId}
          onNavigate={onNavigate}
          variant="sidebar"
          nodeContents={nodeContents}
          collapsed={sidebarCollapsed}
        />
        {/* Collapse toggle — circular button centered on the sidebar's right
            edge line, with gold outlines to match the progress ring. */}
        <button
          type="button"
          onClick={toggleSidebar}
          aria-label={sidebarCollapsed ? 'Expand section list' : 'Collapse section list'}
          aria-expanded={!sidebarCollapsed}
          title={sidebarCollapsed ? 'Expand section list' : 'Collapse section list'}
          className="group absolute top-6 right-0 translate-x-1/2 z-20 w-7 h-7 rounded-full
            bg-dark-elevated border border-gold-500 shadow-[0_0_12px_rgba(240,165,0,0.25)]
            flex items-center justify-center cursor-pointer
            text-gold-500 hover:text-dark-bg hover:bg-gold-500 hover:border-gold-400
            hover:shadow-glow-gold
            transition-all duration-150"
        >
          <ChevronLeft
            size={14}
            strokeWidth={2.5}
            className={`transition-transform duration-200 ${sidebarCollapsed ? 'rotate-180' : ''}`}
          />
        </button>
      </div>

      {/* Progress bar (mobile) */}
      <div className="lg:hidden sticky top-0 z-30">
        <ProgressTracker path={path} currentNodeId={currentNodeId} onNavigate={onNavigate} variant="bar" />
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 py-8">
          {path.nodes.map((node, i) => {
            const content = nodeContents.get(node.nodeId);

            return (
              <motion.section
                key={node.nodeId}
                ref={(el: HTMLDivElement | null) => {
                  if (el) sectionRefs.current.set(node.nodeId, el);
                }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                className={`py-12 ${i < path.nodes.length - 1 ? 'border-b border-dark-border' : ''}`}
              >
                {/* Section number */}
                <p className="text-xs text-content-dim font-body uppercase tracking-wider mb-4">
                  Section {i + 1} of {path.nodes.length}
                  {node.nodeType === 'checkpoint' && ' · Checkpoint'}
                </p>

                {content ? (
                  <NodeContentRenderer
                    nodeContent={content}
                    node={node}
                    pathId={path.pathId}
                  />
                ) : (
                  <div className="py-8 text-center text-content-muted font-body text-sm">
                    Content loading...
                  </div>
                )}
              </motion.section>
            );
          })}

          {/* Navigation */}
          <div className="flex items-center justify-between py-8">
            {prevNode ? (
              <button
                onClick={() => {
                  if (currentNodeId) onComplete(currentNodeId);
                  onNavigate(prevNode.nodeId);
                }}
                className="text-sm text-content-muted hover:text-content transition-colors font-body flex items-center gap-1 cursor-pointer"
              >
                <ArrowLeft size={14} /> Previous
              </button>
            ) : <div />}

            {nextNode ? (
              <Button
                onClick={() => {
                  if (currentNodeId) {
                    // Track skip if node wasn't completed (no checkpoint score, no interaction)
                    if (!path.progress.completedNodes.includes(currentNodeId)) {
                      const skippedNode = path.nodes.find((n) => n.nodeId === currentNodeId);
                      trackEvent({
                        name: ANALYTICS_EVENTS.LEARNING_SECTION_SKIPPED,
                        properties: {
                          ...getBaseProperties(),
                          pathId: path.pathId,
                          nodeId: currentNodeId,
                          nodeType: skippedNode?.nodeType ?? 'learn',
                          contentFormat: skippedNode?.contentFormat ?? 'textual',
                        },
                      }).catch(() => {});
                    }
                    onComplete(currentNodeId);
                  }
                  onNavigate(nextNode.nodeId);
                }}
              >
                Next <ArrowRight size={14} />
              </Button>
            ) : (
              <Button variant="gold" onClick={() => { if (currentNodeId) onComplete(currentNodeId); }}>
                Complete Path
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
